import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Award, Shield, Star, Crown, PieChart } from 'lucide-react';
import { adminApi } from '@/utils/api';

export const SubscribersManager = () => {
    const [subscribers, setSubscribers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchSubscribers = async () => {
        setLoading(true);
        try {
            const res = await adminApi.getSubscriberStats();
            const data = await res.json();
            if (data.success) setSubscribers(data.subscribers);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        fetchSubscribers();
    }, []);

    const getLevelIcon = (name: string) => {
        const n = name.toLowerCase();
        if (n.includes('vip')) return <Crown size={16} className="text-yellow-400" />;
        if (n.includes('verified')) return <Shield size={16} className="text-blue-400" />;
        if (n.includes('basic')) return <Star size={16} className="text-green-400" />;
        return <Award size={16} className="text-white/20" />;
    };

    const getLevelColor = (name: string) => {
        const n = name.toLowerCase();
        if (n.includes('vip')) return '#F59E0B';
        if (n.includes('verified')) return '#3390EC';
        if (n.includes('basic')) return '#10B981';
        return '#6B7280';
    };

    const totalUsers = subscribers.reduce((acc, curr) => acc + curr.user_count, 0);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-white/40 uppercase text-xs font-bold tracking-wider flex items-center gap-2">
                    <PieChart size={14} /> Subscriber Demographics
                </h2>
                <div className="text-[10px] font-black uppercase text-tg-hint">Total Hub: <span className="text-white">{totalUsers} Users</span></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading ? (
                    <div className="col-span-2 p-12 text-center text-white/20 animate-pulse">
                        <Users size={24} className="animate-spin mx-auto mb-2" />
                        <span className="text-xs font-bold uppercase">Scanning User Base...</span>
                    </div>
                ) : subscribers.map((level, i) => {
                    const percentage = totalUsers > 0 ? (level.user_count / totalUsers) * 100 : 0;
                    const color = getLevelColor(level.level_name);
                    
                    return (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.05 }}
                            className="bg-white/5 border border-white/5 p-5 rounded-[24px] relative overflow-hidden group hover:bg-white/10 transition-colors"
                        >
                            <div className="absolute -right-4 -top-4 w-24 h-24 opacity-5 rounded-full blur-3xl transition-all" style={{ backgroundColor: color }} />
                            
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center border border-white/5">
                                        {getLevelIcon(level.level_name)}
                                    </div>
                                    <div>
                                        <div className="text-sm font-black uppercase tracking-tight">{level.level_name}</div>
                                        <div className="text-[10px] text-white/40 font-bold uppercase">{level.user_count} Members</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xl font-black font-mono" style={{ color }}>{percentage.toFixed(1)}%</div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                                    <motion.div 
                                        initial={{ width: 0 }}
                                        animate={{ width: `${percentage}%` }}
                                        className="h-full rounded-full"
                                        style={{ backgroundColor: color }}
                                    />
                                </div>
                                <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-white/20">
                                    <span>Relative Density</span>
                                    <span>{level.user_count} Souls</span>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <div className="bg-blue-500/5 border border-blue-500/20 p-6 rounded-[32px] flex items-center gap-6">
                <div className="w-16 h-16 rounded-3xl bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/10">
                    <TrendingUp size={32} />
                </div>
                <div className="flex-1">
                    <h4 className="text-sm font-black uppercase tracking-widest text-blue-400 mb-1">Growth Intelligence</h4>
                    <p className="text-xs text-white/50 leading-relaxed">
                        User distribution is automatically calculated based on <span className="text-white">XP metrics</span> and <span className="text-white">Subscription Tiers</span>. Higher levels indicate increased platform engagement and higher transaction volume potential.
                    </p>
                </div>
            </div>
        </div>
    );
};
